import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  CurrentUser,
  ModalProps,
  ListResponseData,
  MenuResponse,
  ProductComment,
  ProductInstance,
  ProductRelated,
  OgData,
  GearResponse,
} from 'types';
import { postGearData } from 'api';
import { ShareModal } from 'containers/MyGear/ShareModal';
import { CustomerPageWrapper } from 'containers/CustomerPageWrapper';
import { Snackbar, SnackbarProps } from 'components/Snackbar';
import { Modal } from 'components/Modal';
import { Notification } from 'components/FormNotification';
import { checkIfFullyAuthorized, checkIfSignUpIncomplete, checkIsAdmin, openMobileApp } from 'utils/services';
import { PageBreadcrumbs } from './PageBreadcrambs';
import { PageSlider } from './PageSlider';
import { PageComments } from './PageComments';
import { PageOwnerInfo } from './PageOwnerInfo';
import { PageGearInfo } from './PageGearInfo';
import { PageControls } from './PageControls';
import { PageGearDetails } from './PageGearDetails';
import { PageRelated } from './PageRelated';
import { PageLikeGears } from './PageLikeGears';

import styles from './ProductPage.module.scss';
import { getPageH1Title } from '../../utils/seoFormatters';
import { SimilarBrands } from './SimilarBrands';
import { TopSellers } from './TopSellers';
import { PRPostsReviews } from './PRPostsReviews';

export type ProductPageProps = {
  comments: ListResponseData<ProductComment[]>;
  query?: Record<string, string>;
  ldJsonData?: string;
  menuData: MenuResponse;
  ogData?: OgData[];
  product: ProductInstance;
  related: ProductRelated[];
  likeGears?: null | GearResponse;
  likeSizes?: null | GearResponse;
  similarBrands?: null | any;
  topSellers?: null | any,
  topAmbassadors?: null | any,
  slug: string;
  user?: CurrentUser;
};

export const ProductPage = ({
  comments,
  query,
  ldJsonData,
  menuData,
  ogData,
  product,
  related,
  likeGears,
  likeSizes,
  similarBrands,
  topSellers,
  topAmbassadors,
  externalReviews,
  prPosts,
  slug,
  user: currentUser,
}: ProductPageProps) => {
  const router = useRouter();
  const { to_comments } = query || {};
  const [snackNotification, setSnackNotification] = useState<SnackbarProps | null>(null);
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const isFullyAuthorized = checkIfFullyAuthorized(currentUser);
  const isSignUtProcessIncomplete = checkIfSignUpIncomplete(currentUser);
  const {
    brand,
    category,
    colors,
    condition,
    description,
    favorites,
    id,
    in_my_favorites: inMyFavorites,
    is_sold: isSold,
    hourly_price: hourlyPrice,
    hourly_rent_enabled: hourlyEnabled,
    media,
    meeting,
    original_price: originalPrice,
    posted_at: postedAt,
    price,
    sizes,
    sizes_gide_file: sizeGuide,
    type,
    title,
    user: productOwner,
    technical_specification: technicalSpecification,
    currency,
  } = product;
  const isCharity = useMemo(() => product.charity_percent===100, [product.charity_percent])
  const productInfo = {
    image: media.images[0] || '/',
    title: title,
    owner: productOwner.username,
  };
  const isOwner = productInfo.owner === currentUser?.username;
  const isAdmin = checkIsAdmin(currentUser);
  const [isFavorite, setIsFavorite] = useState(inMyFavorites);
  const showControls = !isOwner && !isAdmin;

  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [availableVariants, setAvailableVariants] = useState(product.variants)

  useEffect(() => {
    let cheapestVariant = product.variants?.find(v => v.in_stock>0) || product.variants?.[0]
    product.variants?.map(v => {
      // @ts-ignore
      if(parseFloat(v.price)<=parseFloat(cheapestVariant.price) && v.in_stock)
        cheapestVariant = v
    })
    if(cheapestVariant) {
      // @ts-ignore
      product.options = cheapestVariant.options.map(({id}) => {
        return product.options.find(o => o.id===id)
      })
      setSelectedOptions(cheapestVariant.options.map(o => o.value))
      setAvailableVariants([cheapestVariant])
    }
  }, [product.variants])


  const priceObject = useMemo(() => {
    return availableVariants?.length===1 ? availableVariants[0] : product
  }, [availableVariants, product])

  useLayoutEffect(() => {
    !!window.gtag && window.gtag('event', `view_${type}_product`, { event_category: 'order' })
    openMobileApp(`gears/${slug}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const redirectToAuth = () => {
    if (isSignUtProcessIncomplete) {
      return router.push(`/sign-up?redirect=/gear/${slug}`);
    } else {
      return router.push(`/sign-in?redirect=/gear/${slug}`);
    }
  };

  const handleFavoriteButton = async () => {
    const response = await postGearData(slug, 'favorite', { is_favorite: !isFavorite });
    if (response.ok) {
      setIsFavorite(!isFavorite);
    } else {
      setSnackNotification({
        mode: 'error',
        header: response.message || 'Sorry, server responds with error. Please, try later',
      });
    }
  };

  const handleShareModalOpen = (slug: string) => {
    setModalProps({
      child: (
        <ShareModal
          dividerText="or"
          isGearOwner={isOwner}
          isSold={isSold}
          slug={slug}
          shareToFollowersBtn
          user={currentUser}
        />
      ),
      title: 'Share this gear',
      onCoverClick: () => {
        setModalProps(null);
      },
      withCrossIcon: true,
    });
  };

  const carouselData = useMemo(() => {
    if(availableVariants?.length===1 && !!availableVariants[0].images.length && product.use_variants && product.media) {
      return {video: media.video, images: availableVariants[0].images.map(({original} : {original: string}) => original)}
    } else if (product.media) {
      return {...media, images: [...media.images, ...media.images, ...media.images]}
    } else return undefined
  }, [media, availableVariants])

  return (
    <CustomerPageWrapper
      headTitle={category ? `${title} - ${category.title} | SENDY` : `${title} | SENDY`}
      ldJsonData={ldJsonData}
      menuData={menuData}
      ogData={ogData}
      user={currentUser}
      hiddenComponentProps={{ title: getPageH1Title('gear', { title: product.title }) }}
      appliedFilters={{ type }}
    >
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.content}>
            <PageBreadcrumbs category={category} />
            <div className={styles.mainContent}>
              {media && <PageSlider media={carouselData} isSold={isSold} isCharity={isCharity} />}
              <div className={styles.notForMobile}>
                <PageComments
                  isAuth={isFullyAuthorized}
                  favorites={favorites}
                  inMyFavorites={isFavorite}
                  comments={comments}
                  setSnackNotification={setSnackNotification}
                  setModalProps={setModalProps}
                  user={currentUser}
                  slug={slug}
                  productInfo={productInfo}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                  toComments={!!to_comments}
                  redirectToAuth={redirectToAuth}
                  handleFavoriteButton={isFullyAuthorized ? handleFavoriteButton : redirectToAuth}
                  handleShareButton={handleShareModalOpen}
                />
              </div>
            </div>
            <div className={styles.sidebar}>
              {postedAt * 1000 > Date.now() && (
                <Notification
                  header='Not yet available for purchase'
                  message={`This item will be dropped on ${new Date(postedAt * 1000).toLocaleString()}`}
                />
              )}
              <PageGearInfo
                country={meeting?.country || productOwner.country}
                description={description}
                handleFavoriteButton={isFullyAuthorized ? handleFavoriteButton : redirectToAuth}
                handleShareButton={handleShareModalOpen}
                hourlyPrice={hourlyEnabled ? hourlyPrice : 0}
                inMyFavorites={isFavorite}
                isAdmin={isAdmin}
                isAuth={isFullyAuthorized}
                isOwner={isOwner}
                originalPrice={priceObject.original_price}
                price={priceObject.price}
                currency={currency}
                slug={slug}
                title={title}
                type={type}
                availableVariants={availableVariants}
              >
                {showControls && (
                  <PageControls
                    type={type}
                    isAuth={isFullyAuthorized}
                    redirectToAuth={redirectToAuth}
                    setModalProps={setModalProps}
                    title={title}
                    price={priceObject.price}
                    minOffer={priceObject.min_offer_price_percent}
                    sizes={sizes}
                    brand={brand}
                    image={media.images[0]}
                    id={id}
                    isSold={type === 'sell' && isSold}
                    setSnackNotification={setSnackNotification}
                    selectedVariant={availableVariants?.length === 1 ? availableVariants[0] : undefined}
                    disabled={product.use_variants && (availableVariants?.length !== 1 || availableVariants[0].in_stock === 0)}
                    isOutOfStock={(availableVariants?.length === 1 && availableVariants[0].in_stock === 0)}
                    offerAllowed={product.user.accept_offers && (product.use_variants ? (availableVariants?.length === 1 && availableVariants[0].offer_allowed) : product.offer_allowed)}
                  />
                )}
              </PageGearInfo>
              <PageGearDetails
                brand={brand}
                colors={colors}
                condition={condition}
                sizes={sizes}
                sizeGuide={sizeGuide}
                technicalSpecification={technicalSpecification}
                category={category}
                useVariants={product.use_variants}
                options={product.options}
                variants={product.variants}
                selectedOptions={selectedOptions}
                availableVariants={availableVariants}
                setSelectedOptions={setSelectedOptions}
                setAvailableVariants={setAvailableVariants}
              />
              <div className={styles.notForMobile}>
                <div className={styles.subtitle}>Seller Info</div>
                <PageOwnerInfo
                  meeting={meeting}
                  productOwner={productOwner}
                  postedAt={postedAt}
                  isAuth={isFullyAuthorized}
                  redirectToAuth={redirectToAuth}
                  gearId={id}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                  isRent={type === 'rent'}
                />
              </div>
              <div className={styles.forMobileOnly}>
                <div className={styles.subtitle}>Seller Info</div>
                <PageOwnerInfo
                  meeting={meeting}
                  productOwner={productOwner}
                  postedAt={postedAt}
                  isAuth={isFullyAuthorized}
                  redirectToAuth={redirectToAuth}
                  gearId={id}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                  isRent={type === 'rent'}
                />
              </div>
              <div className={styles.notForDesktop}>
                <PageComments
                  isAuth={isFullyAuthorized}
                  favorites={favorites}
                  inMyFavorites={isFavorite}
                  comments={comments}
                  setSnackNotification={setSnackNotification}
                  setModalProps={setModalProps}
                  user={currentUser}
                  slug={slug}
                  productInfo={productInfo}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                  toComments={!!to_comments}
                  redirectToAuth={redirectToAuth}
                  handleFavoriteButton={isFullyAuthorized ? handleFavoriteButton : redirectToAuth}
                  handleShareButton={isFullyAuthorized ? handleShareModalOpen : redirectToAuth}
                />
              </div>
            </div>
          </div>
          {likeGears && (
            <PageLikeGears
              gears={likeGears}
              likeGearSlug={slug}
              isFullyAuth={isFullyAuthorized}
              redirectToAuth={redirectToAuth}
              setSnackbarMessage={setSnackNotification}
            />
          )}
          <PageRelated related={(related && related?.filter((item) => item.id !== id)) || []} />
          {!isFullyAuthorized && <PRPostsReviews posts={prPosts?.data || []} reviews={externalReviews?.data || []} />}
          {similarBrands && (<SimilarBrands brands={similarBrands.data || []} />)}
          {likeSizes && (<PageRelated title={'Similar Sizes'} related={likeSizes.hits || []} />)}
          {topSellers && (
            <TopSellers
              sellers={topSellers}
              currentUser={currentUser}
              setSnackbarMessage={setSnackNotification}
            />
          )}
          {topAmbassadors && (
            <TopSellers
              title={'Top PROs'}
              sellers={topAmbassadors}
              currentUser={currentUser}
              setSnackbarMessage={setSnackNotification}
            />
          )}
          {snackNotification && <Snackbar {...snackNotification} onClose={() => setSnackNotification(null)} />}
          {!!modalProps ? <Modal {...modalProps} /> : null}
        </div>
      </div>
    </CustomerPageWrapper>
  );
};
